import { forwardRef, memo, type ReactNode } from 'react';
import isString from 'lodash/isString';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Paper, { type PaperProps } from '@mui/material/Paper';
import Skeleton, { type SkeletonProps } from '@mui/material/Skeleton';
// SCSS imports
import { overlayClear } from '../styles/modules/Overlay.module.scss';
import { skeletonPaper, skeleton, heightNormal, height100 } from './LoadingPlaceholder.module.scss';

type LoadingPlaceholderProps = {
  readonly flat?: boolean;
  readonly fullHeight?: boolean;
  readonly message?: string | ReactNode;
  readonly className?: string;
  readonly paperSx?: PaperProps['sx'];
  readonly skeletonSx?: SkeletonProps['sx'];
};

const LoadingPlaceholder = forwardRef<HTMLDivElement, LoadingPlaceholderProps>(({
  flat = false,
  fullHeight = false,
  message,
  className,
  paperSx,
  skeletonSx
}, ref) => (
  <Paper
      ref={ref}
      sx={paperSx}
      elevation={flat ? 0 : undefined}
      className={clsx(skeletonPaper, {
        [height100]: fullHeight
      })}
  >
    <Skeleton
        sx={skeletonSx}
        variant={flat ? 'rectangular' : 'rounded'}
        className={clsx(skeleton, className, {
          [heightNormal]: !className && !fullHeight,
          [height100]: fullHeight
        })}
    />
    {message ? (
      <div className={overlayClear}>
        {(isString(message) && (
          <Box textAlign="center" p={4} color="text.secondary">
            <FormattedMessage id={message} defaultMessage={message}/> 
          </Box>
        )) || message}
      </div>
    ) : undefined}
  </Paper>
));

LoadingPlaceholder.displayName = 'LoadingPlaceholder';

export default memo(LoadingPlaceholder);
